import React, { useEffect, useState } from "react";
import {
    DashboardOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";

import {
    canSyncHealthData,
    getHealthHistory,
    saveHealthData,
    deleteHealthData
} from "../../api/healthTracking";

export default function Spo2() {
    const [spo2, setSpo2] = useState("");
    const [heartRate, setHeartRate] = useState("");
    const [note, setNote] = useState("");
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const syncWithAccount = canSyncHealthData();

    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);

            if (syncWithAccount) {
                const result = await getHealthHistory("spo2");

                if (result.success) {
                    const data = result.data.map((item) => ({
                        id: item.health_tracking_id,
                        spo2: item.tracking_data?.spo2,
                        heartRate: item.tracking_data?.heartRate,
                        note: item.tracking_data?.note,
                        date: item.tracking_data?.date,
                        time: item.tracking_data?.time,
                        database: true
                    }));

                    setHistory(data);
                } else {
                    setHistory([]);
                }
            } else {
                const savedData = localStorage.getItem("spo2History");

                if (savedData) {
                    try {
                        setHistory(JSON.parse(savedData));
                    } catch (error) {
                        setHistory([]);
                    }
                } else {
                    setHistory([]);
                }
            }

            setLoading(false);
        };

        loadHistory();
    }, [syncWithAccount]);

    const reloadAccountHistory = async () => {
        const result = await getHealthHistory("spo2");

        if (!result.success) {
            return false;
        }

        const data = result.data.map((item) => ({
            id: item.health_tracking_id,
            spo2: item.tracking_data?.spo2,
            heartRate: item.tracking_data?.heartRate,
            note: item.tracking_data?.note,
            date: item.tracking_data?.date,
            time: item.tracking_data?.time,
            database: true
        }));

        setHistory(data);

        return true;
    };

    const handleSave = async () => {
        if (!spo2) {
            alert("Vui lòng nhập chỉ số SpO₂.");
            return;
        }

        const spo2Number = Number(spo2);
        const heartRateNumber = heartRate
            ? Number(heartRate)
            : null;

        if (
            spo2Number <= 0 ||
            spo2Number > 100 ||
            (heartRate && heartRateNumber <= 0)
        ) {
            alert("Vui lòng nhập chỉ số hợp lệ.");
            return;
        }

        const now = new Date();

        const newRecord = {
            id: Date.now(),
            spo2: spo2Number,
            heartRate: heartRateNumber,
            note: note.trim(),
            date: now.toLocaleDateString("vi-VN"),
            time: now.toLocaleTimeString("vi-VN", {
                hour: "2-digit",
                minute: "2-digit"
            })
        };

        if (syncWithAccount) {
            setSaving(true);

            const result = await saveHealthData(
                "spo2",
                {
                    spo2: newRecord.spo2,
                    heartRate: newRecord.heartRate,
                    note: newRecord.note,
                    date: newRecord.date,
                    time: newRecord.time
                }
            );

            if (!result.success) {
                setSaving(false);

                alert(
                    "Không thể lưu dữ liệu vào tài khoản. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();

            setSaving(false);
        } else {
            const newHistory = [
                newRecord,
                ...history
            ];

            setHistory(newHistory);

            localStorage.setItem(
                "spo2History",
                JSON.stringify(newHistory)
            );
        }

        setSpo2("");
        setHeartRate("");
        setNote("");
    };

    const handleDelete = async (id) => {
        if (syncWithAccount) {
            const result = await deleteHealthData(id);

            if (!result.success) {
                alert(
                    "Không thể xóa dữ liệu. Vui lòng thử lại."
                );
                return;
            }

            await reloadAccountHistory();
        } else {
            const newHistory = history.filter(
                (item) => item.id !== id
            );

            setHistory(newHistory);

            localStorage.setItem(
                "spo2History",
                JSON.stringify(newHistory)
            );
        }
    };

    return (
        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">

            <a
                href="/tienich/suc-khoe"
                className="inline-flex items-center text-blue-600 font-semibold text-sm mb-4"
            >
                <ArrowLeftOutlined className="mr-2" />
                Theo dõi sức khỏe
            </a>

            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">
                        <DashboardOutlined />
                    </div>

                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">
                            THEO DÕI SỨC KHỎE
                        </p>

                        <h1 className="text-[25px] md:text-3xl font-bold">
                            SpO₂
                        </h1>

                    </div>

                </div>

                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal">
                    Ghi lại độ bão hòa oxy trong máu và nhịp tim để theo dõi sự thay đổi theo thời gian.
                </p>

            </div>

            {syncWithAccount ? (

                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-green-700 font-semibold">
                        ✓ Dữ liệu đang được lưu theo tài khoản
                    </p>

                    <p className="text-[12px] text-green-600 mt-1">
                        Bạn có thể đăng nhập tài khoản này trên thiết bị khác để xem lại lịch sử.
                    </p>

                </div>

            ) : (

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-yellow-700 font-semibold">
                        Dữ liệu đang được lưu trên thiết bị này
                    </p>

                    <p className="text-[12px] text-yellow-600 mt-1">
                        Đăng nhập bằng tài khoản có mật khẩu để lưu lịch sử theo tài khoản.
                    </p>

                </div>

            )}

            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Nhập chỉ số
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    Nhập kết quả đo từ máy đo SpO₂ của bạn.
                </p>

                <div className="mt-5">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        SpO₂
                    </label>

                    <div className="relative">

                        <input
                            type="number"
                            value={spo2}
                            onChange={(e) =>
                                setSpo2(e.target.value)
                            }
                            placeholder="Ví dụ: 98"
                            min="1"
                            max="100"
                            className="w-full h-12 border rounded-xl px-3 pr-14 outline-none focus:border-blue-500"
                        />

                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            %
                        </span>

                    </div>

                </div>

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nhịp tim
                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>
                    </label>

                    <div className="relative">

                        <input
                            type="number"
                            value={heartRate}
                            onChange={(e) =>
                                setHeartRate(e.target.value)
                            }
                            placeholder="Ví dụ: 72"
                            className="w-full h-12 border rounded-xl px-3 pr-24 outline-none focus:border-blue-500"
                        />

                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            lần/phút
                        </span>

                    </div>

                </div>

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ghi chú
                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>
                    </label>

                    <textarea
                        value={note}
                        onChange={(e) =>
                            setNote(e.target.value)
                        }
                        placeholder="Ví dụ: đo khi đang nghỉ ngơi..."
                        rows={3}
                        className="w-full border rounded-xl px-3 py-3 outline-none focus:border-blue-500 resize-none"
                    />

                </div>

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full mt-5 h-12 rounded-xl text-white font-bold ${
                        saving
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >
                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu chỉ số"}
                </button>

            </div>

            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Lịch sử gần đây
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    {syncWithAccount
                        ? "Các chỉ số được lưu theo tài khoản của bạn."
                        : "Các chỉ số bạn đã lưu trên thiết bị này."}
                </p>

                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">
                            Đang tải dữ liệu...
                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <DashboardOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">
                            Chưa có dữ liệu
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                            Chỉ số SpO₂ bạn lưu sẽ xuất hiện tại đây.
                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4"
                            >

                                <div className="flex items-start justify-between">

                                    <div>

                                        <div className="flex items-end">

                                            <p className="text-2xl font-bold text-gray-900">
                                                {item.spo2}
                                            </p>

                                            <span className="text-xs text-gray-400 ml-1 mb-1">
                                                %
                                            </span>

                                        </div>

                                        {item.heartRate && (
                                            <p className="text-sm text-gray-600 mt-1">
                                                Nhịp tim: {item.heartRate} lần/phút
                                            </p>
                                        )}

                                        <p className="text-xs text-gray-400 mt-1">
                                            {item.time} - {item.date}
                                        </p>

                                    </div>

                                    <button
                                        onClick={() =>
                                            handleDelete(item.id)
                                        }
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center"
                                        aria-label="Xóa chỉ số"
                                    >
                                        <DeleteOutlined />
                                    </button>

                                </div>

                                {item.note && (
                                    <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">
                                        <p className="text-xs text-gray-500">
                                            {item.note}
                                        </p>
                                    </div>
                                )}

                            </div>

                        ))}

                    </div>

                )}

            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">
                    Lưu ý
                </p>

                {syncWithAccount ? (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu SpO₂ được lưu theo tài khoản đang đăng nhập và có thể được tải lại trên thiết bị khác khi sử dụng cùng tài khoản.
                    </p>

                ) : (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu hiện được lưu trên trình duyệt của thiết bị bạn đang sử dụng.
                        Nếu xóa dữ liệu trình duyệt hoặc đổi thiết bị, lịch sử có thể không còn.
                    </p>

                )}

                <p className="text-[13px] text-gray-500 leading-5 mt-2">
                    Tiện ích này dùng để ghi lại và theo dõi thông tin sức khỏe,
                    không dùng để tự chẩn đoán hoặc thay thế đánh giá của nhân viên y tế.
                </p>

            </div>

        </div>
    );
}